import React, { useState } from 'react';

const TYPE_OPTIONS = ['MCQ', 'true/false'];

const QuizForm = ({ courses = [], initialData, onSave, onCancel, saving }) => {
  const [courseId, setCourseId] = useState(initialData?.courseId || '');
  const [type, setType] = useState(initialData?.type || 'MCQ');
  const [question, setQuestion] = useState(initialData?.question || '');
  const [optionA, setOptionA] = useState(initialData?.optionA || '');
  const [optionB, setOptionB] = useState(initialData?.optionB || '');
  const [optionC, setOptionC] = useState(initialData?.optionC || '');
  const [optionD, setOptionD] = useState(initialData?.optionD || '');
  const [correctAns, setCorrectAns] = useState(initialData?.correctAns || '');
  const [marks, setMarks] = useState(initialData?.marks ?? 1);

  const isEditing = !!initialData?.id;
  const isTrueFalse = type === 'true/false';

  const handleTypeChange = (newType) => {
    setType(newType);
    if (newType === 'true/false') {
      setOptionA('True');
      setOptionB('False');
      setOptionC('');
      setOptionD('');
      setCorrectAns('');
    } else {
      setOptionA('');
      setOptionB('');
      setOptionC('');
      setOptionD('');
      setCorrectAns('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!courseId) { alert('Please select a course'); return; }
    if (!question.trim()) { alert('Question is required'); return; }

    if (isTrueFalse) {
      if (!correctAns) { alert('Please select the correct answer'); return; }
      onSave({
        courseId,
        type,
        question: question.trim(),
        optionA: 'True',
        optionB: 'False',
        optionC: '',
        optionD: '',
        correctAns,
        marks: Number(marks) || 1
      });
    } else {
      if (!optionA.trim() || !optionB.trim()) { alert('Options A and B are required'); return; }
      if (!correctAns) { alert('Please select the correct answer'); return; }
      onSave({
        courseId,
        type,
        question: question.trim(),
        optionA: optionA.trim(),
        optionB: optionB.trim(),
        optionC: optionC.trim(),
        optionD: optionD.trim(),
        correctAns,
        marks: Number(marks) || 1
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
        <select
          value={courseId}
          onChange={(e) => setCourseId(e.target.value)}
          disabled={isEditing}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
          required
        >
          <option value="">-- Select a Course --</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Question Type *</label>
        <select
          value={type}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{t === 'true/false' ? 'True / False' : t}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          placeholder="Enter the question text"
          required
        />
      </div>

      <div className="space-y-4">
        <p className="text-sm font-medium text-gray-700">Options</p>

        {isTrueFalse ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
              <input
                type="radio"
                name="correctAns"
                value="True"
                checked={correctAns === 'True'}
                onChange={(e) => setCorrectAns(e.target.value)}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-800 font-medium">True</span>
            </div>
            <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
              <input
                type="radio"
                name="correctAns"
                value="False"
                checked={correctAns === 'False'}
                onChange={(e) => setCorrectAns(e.target.value)}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-800 font-medium">False</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['A', 'B', 'C', 'D'].map((label) => {
              const value = label === 'A' ? optionA : label === 'B' ? optionB : label === 'C' ? optionC : optionD;
              const setter = label === 'A' ? setOptionA : label === 'B' ? setOptionB : label === 'C' ? setOptionC : setOptionD;
              return (
                <div key={label} className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 w-5 flex-shrink-0">{label}.</span>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder={`Option ${label}`}
                    required={label === 'A' || label === 'B'}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!isTrueFalse && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer *</label>
          <select
            value={correctAns}
            onChange={(e) => setCorrectAns(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            required
          >
            <option value="">-- Select Correct Answer --</option>
            <option value="A">Option A</option>
            <option value="B">Option B</option>
            <option value="C">Option C</option>
            <option value="D">Option D</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Marks</label>
        <input
          type="number"
          min={1}
          value={marks}
          onChange={(e) => setMarks(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default QuizForm;
